import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import { EntityType } from '../types/floorLayout';
import { parkingTypeService } from '../services/parkingService';
import '../styles/FloorLayoutEditor.css';

interface AddSpaceModalProps {
  isOpen: boolean;
  entityType: EntityType;
  workspaceId: string;
  floorId: string;
  floorName: string;
  projectId: string;
  onClose: () => void;
  onSuccess: (newSpace: any) => void;
}

const AddSpaceModal: React.FC<AddSpaceModalProps> = ({
  isOpen,
  entityType,
  workspaceId,
  floorId,
  floorName,
  projectId,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    number: '',
    type: '',
    status: 'AVAILABLE',
    isPremium: false,
    positionX: 100,
    positionY: 100
  });
  const [saving, setSaving] = useState(false);
  const [parkingTypes, setParkingTypes] = useState<any[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  // Fetch types when modal opens
  useEffect(() => {
    if (isOpen) {
      if (entityType === 'parking') {
        fetchParkingTypes();
      } else if (entityType === 'locker') {
        fetchLockerTypes();
      } else if (entityType === 'bicycle') {
        fetchBicycleTypes();
      }
    }
  }, [isOpen, entityType, workspaceId, projectId]);

  const fetchParkingTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await parkingTypeService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setParkingTypes(response.parkingTypes || []);

      // Set default type if available
      if (response.parkingTypes && response.parkingTypes.length > 0 && !formData.type) {
        setFormData(prev => ({ ...prev, type: response.parkingTypes[0].name }));
      }
    } catch (error) {
      console.error('Error fetching parking types:', error);
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchLockerTypes = async () => {
    try {
      setLoadingTypes(true);
      const { lockerTypeService } = await import('../services/lockerService');
      const response = await lockerTypeService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setParkingTypes(response.lockerTypes || []); // Reuse parkingTypes state for simplicity

      // Set default type if available
      if (response.lockerTypes && response.lockerTypes.length > 0 && !formData.type) {
        setFormData(prev => ({ ...prev, type: response.lockerTypes[0].name }));
      }
    } catch (error) {
      console.error('Error fetching locker types:', error);
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchBicycleTypes = async () => {
    try {
      setLoadingTypes(true);
      const { bicycleTypeService } = await import('../services/bicycleService');
      const response = await bicycleTypeService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setParkingTypes(response.bicycleTypes || []); // Reuse parkingTypes state for simplicity

      // Set default type if available
      if (response.bicycleTypes && response.bicycleTypes.length > 0 && !formData.type) {
        setFormData(prev => ({ ...prev, type: response.bicycleTypes[0].name }));
      }
    } catch (error) {
      console.error('Error fetching bicycle types:', error);
    } finally {
      setLoadingTypes(false);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.number.trim()) {
      toast.error('Space number is required');
      return;
    }

    setSaving(true);
    try {
      const spaceData = {
        number: formData.number,
        type: formData.type,
        status: formData.status,
        isPremium: formData.isPremium,
        floorId,
        projectId,
        spaceObject: {
          left: formData.positionX,
          top: formData.positionY,
          width: entityType === 'parking' ? 150 : entityType === 'bicycle' ? 20 : 15,
          height: entityType === 'parking' ? 75 : entityType === 'bicycle' ? 20 : 15,
          angle: 0
        }
      };

      let newSpace;
      if (entityType === 'parking') {
        const { parkingFloorService } = await import('../services/parkingService');
        const response = await parkingFloorService.createSpace(workspaceId, spaceData);
        newSpace = response.parkingSpace;
      } else if (entityType === 'locker') {
        const { lockerSpaceService } = await import('../services/lockerService');
        const response = await lockerSpaceService.create(workspaceId, spaceData);
        newSpace = response.lockerSpace;
      } else if (entityType === 'bicycle') {
        const { bicycleSpaceService } = await import('../services/bicycleService');
        const response = await bicycleSpaceService.create(workspaceId, spaceData);
        newSpace = response.bicycleSpace;
      }

      if (newSpace) {
        onSuccess(newSpace);
      }
    } catch (error: any) {
      console.error('Error creating space:', error);
      toast.error(error.response?.data?.message || 'Failed to create space');
    } finally {
      setSaving(false);
    }
  };

  const getEntityLabel = () => {
    if (entityType === 'parking') return 'Parking Space';
    if (entityType === 'bicycle') return 'Bicycle Space';
    return 'Locker';
  };

  return (
    <div className="floor-layout-modal-overlay" onClick={onClose}>
      <div className="floor-layout-modal" onClick={(e) => e.stopPropagation()}>
        <div className="floor-layout-modal-header">
          <h3>Add New {getEntityLabel()}</h3>
          <button className="floor-layout-close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="floor-layout-modal-body">
            <div className="model-details-field">
              <label>Space Number *</label>
              <input
                type="text"
                className="form-input"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                placeholder="e.g., P-101"
                required
              />
            </div>

            <div className="model-details-field">
              <label>Type</label>
              {(entityType === 'parking' || entityType === 'locker' || entityType === 'bicycle') ? (
                loadingTypes ? (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#6B7280' }}>
                    Loading types...
                  </div>
                ) : (
                  <select
                    className="form-input"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                  >
                    <option value="">Select Type</option>
                    {parkingTypes.map((type) => (
                      <option key={type._id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                )
              ) : (
                <input
                  type="text"
                  className="form-input"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                />
              )}
            </div>

            <div className="model-details-field">
              <label>Status</label>
              <select
                className="form-input"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="AVAILABLE">Available</option>
                <option value="MARKETING HOLD">Marketing Hold</option>
                <option value="SALES HOLD">Sales Hold</option>
                <option value="CUSTOMER SELECTION">Customer Selection</option>
                <option value="CUSTOMER SUBMITTED">Customer Submitted</option>
                <option value="SOLD">Sold</option>
              </select>
            </div>

            <div className="model-details-field">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isPremium}
                  onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                Premium Space
              </label>
            </div>

            <div className="model-details-field">
              <label>Initial Position X</label>
              <input
                type="number"
                className="form-input"
                value={formData.positionX}
                onChange={(e) => setFormData({ ...formData, positionX: parseInt(e.target.value) })}
              />
            </div>

            <div className="model-details-field">
              <label>Initial Position Y</label>
              <input
                type="number"
                className="form-input"
                value={formData.positionY}
                onChange={(e) => setFormData({ ...formData, positionY: parseInt(e.target.value) })}
              />
            </div>
          </div>

          <div className="floor-layout-modal-footer">
            <button
              type="button"
              className="btn btn-secondary btn-md"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md"
              disabled={saving}
            >
              {saving ? 'Adding...' : 'Add Space'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSpaceModal;
